import { twMerge } from 'tailwind-merge';
import { type ElementType, type ReactNode } from 'react';

export type StatCardTone = 'primary' | 'success' | 'warning' | 'error' | 'info';

export interface StatCardProps {
  label: string;
  value: ReactNode;
  Icon: ElementType;
  /** Optional supporting line rendered under the value. */
  hint?: string;
  /** Drives the accent color of the icon badge. */
  tone?: StatCardTone;
}

const TONE_CLASS: Record<StatCardTone, string> = {
  primary: 'bg-primary/10 text-primary',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  error: 'bg-error/10 text-error',
  info: 'bg-info/10 text-info',
};

/**
 * Compact KPI card used across the dashboard-style sections
 * (Finances, Analytics, Monitoring).
 */
export function StatCard(props: StatCardProps) {
  const tone = props.tone ?? 'primary';

  return (
    <div className="card bg-base-100 border border-base-300">
      <div className="card-body flex-row items-center gap-4 p-5">
        <div className={twMerge('rounded-box p-3', TONE_CLASS[tone])}>
          <props.Icon className="w-6 h-6" />
        </div>
        <div className="flex flex-col">
          <span className="text-sm text-base-content/60">{props.label}</span>
          <span className="text-2xl font-bold">{props.value}</span>
          {props.hint ? <span className="text-xs text-base-content/50">{props.hint}</span> : null}
        </div>
      </div>
    </div>
  );
}
